import React, { useState, useEffect } from "react";
import { Table, Input, Button, message, Modal, Form, DatePicker, InputNumber } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import NewLoan from '../../../../modals/newLoan';
import ModalWrapper from '../../../../Modal';
import { getRequest, postRequest } from "../../../../service/index";
import dayjs from "dayjs";

const RequestLoan = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paymentForm] = Form.useForm();

  // Fetch loans from API
  const fetchLoans = async () => {
    setLoading(true);
    try {
      const res = await getRequest("/api/loanpayment");
      if (res && res.success && Array.isArray(res.data)) {
        setLoans(res.data);
      } else {
        setLoans([]);
      }
    } catch (err) {
      message.error("Failed to fetch loans");
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle payment modal open
  const handleOpenPaymentModal = (loan) => {
    setSelectedLoan(loan);
    setIsPaymentModalOpen(true);
    paymentForm.setFieldsValue({
      date: dayjs(),
      amount: 0
    });
  };

  // Handle payment modal close
  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedLoan(null);
    paymentForm.resetFields();
  };

  // Handle payment submission
  const handlePaymentSubmit = async (values) => {
    if (!selectedLoan) return;
    
    setPaymentLoading(true);
    try {
      const paymentData = {
        date: values.date.format("YYYY-MM-DD"),
        amount: values.amount
      };
      
      const res = await postRequest(`/api/loanpayment/${selectedLoan._id}/pay`, paymentData);
      
      if (res && res.success) {
        message.success("Payment processed successfully");
        await fetchLoans(); // Refresh the loans data
        handleClosePaymentModal();
      } else {
        message.error(res?.message || "Failed to process payment");
      }
    } catch (err) {
      message.error("Failed to process payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  // After submitting a new loan, refresh the table
  const handleSubmitLoan = async () => {
    await fetchLoans();
    handleCloseModal();
  };

  // Table columns
  const columns = [
    {
      title: "Employee",
      dataIndex: "employeeId",
      key: "employeeId",
      render: (employeeId) =>
        employeeId && typeof employeeId === "object"
          ? `${employeeId.firstName || ""} ${employeeId.lastName || ""}`.trim()
          : "",
    },
    {
      title: "Loan Date",
      dataIndex: "loanDate",
      key: "loanDate",
      render: (date) => (date ? dayjs(date).format("YYYY-MM-DD") : ""),
    },
    {
      title: "Loan Amount",
      dataIndex: "loanAmount",
      key: "loanAmount",
    },
    {
      title: "Per Month Instalment",
      dataIndex: "perMonthInstalment",
      key: "perMonthInstalment",
    },
    {
      title: "Number of Months",
      dataIndex: "numberOfMonths",
      key: "numberOfMonths",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : "",
    },
    {
      title: "Total Recovery",
      dataIndex: "totalRecovery",
      key: "totalRecovery",
      render: (_, record) => {
        // paidInstalments is expected to be an array of numbers or objects with amount
        if (Array.isArray(record.paidInstalments)) {
          // If each item is a number
          if (record.paidInstalments.length > 0 && typeof record.paidInstalments[0] === "number") {
            return record.paidInstalments.reduce((sum, val) => sum + val, 0);
          }
          // If each item is an object with amount property
          if (record.paidInstalments.length > 0 && typeof record.paidInstalments[0] === "object" && record.paidInstalments[0] !== null) {
            return record.paidInstalments.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
          }
          // Empty array
          return 0;
        }
        // Fallback to old totalRecovery if exists
        return record.totalRecovery || 0;
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => handleOpenPaymentModal(record)}
          className="text-green-600 hover:text-green-800"
          title="Add Payment"
        />
      ),
    },
  ];

  // Filter loans by search
  const filteredLoans = loans.filter((loan) => {
    const employeeName =
      loan.employeeId && typeof loan.employeeId === "object"
        ? `${loan.employeeId.firstName || ""} ${loan.employeeId.lastName || ""}`.toLowerCase()
        : "";
    const loanDate = loan.loanDate ? dayjs(loan.loanDate).format("YYYY-MM-DD").toLowerCase() : "";
    const loanAmount = loan.loanAmount ? loan.loanAmount.toString().toLowerCase() : "";
    const perMonthInstalment = loan.perMonthInstalment ? loan.perMonthInstalment.toString().toLowerCase() : "";
    const numberOfMonths = loan.numberOfMonths ? loan.numberOfMonths.toString().toLowerCase() : "";
    const status = loan.status ? loan.status.toLowerCase() : "";
    // For search, sum paidInstalments for totalRecovery
    let totalRecovery = "";
    if (Array.isArray(loan.paidInstalments)) {
      if (loan.paidInstalments.length > 0 && typeof loan.paidInstalments[0] === "number") {
        totalRecovery = loan.paidInstalments.reduce((sum, val) => sum + val, 0).toString().toLowerCase();
      } else if (loan.paidInstalments.length > 0 && typeof loan.paidInstalments[0] === "object" && loan.paidInstalments[0] !== null) {
        totalRecovery = loan.paidInstalments.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0).toString().toLowerCase();
      } else {
        totalRecovery = "0";
      }
    } else {
      totalRecovery = loan.totalRecovery ? loan.totalRecovery.toString().toLowerCase() : "";
    }

    const searchText = search.toLowerCase();

    return (
      employeeName.includes(searchText) ||
      loanDate.includes(searchText) ||
      loanAmount.includes(searchText) ||
      perMonthInstalment.includes(searchText) ||
      numberOfMonths.includes(searchText) ||
      status.includes(searchText) ||
      totalRecovery.includes(searchText)
    );
  });

  return (
    <>
      <div className="p-3 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_-4px_6px_rgba(0,0,0,0.05)] rounded-md">
        <div className="flex justify-between items-center">
          <div className="flex w-full max-w-md gap-4 items-center">
            <Input
              placeholder="Search"
              className="w-full text-md"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <Button
            type="primary"
            className="bg-primary text-white"
            onClick={handleOpenModal}
          >
            <PlusOutlined /> Create a New
          </Button>
        </div>

        {/* Table */}
        <div className="rounded mt-4">
          <Table
            columns={columns}
            dataSource={filteredLoans.map((loan) => ({
              ...loan,
              key: loan._id,
            }))}
            loading={loading}
            locale={{ emptyText: "No Data" }}
            scroll={{ x: 'max-content' }}
            pagination={{
              total: filteredLoans.length,
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
          />
        </div>
      </div>
      
      {/* New Loan Modal */}
      <ModalWrapper
        open={isModalOpen}
        onRequestClose={handleCloseModal}
        title="Create New Loan"
      >
        <NewLoan onClose={handleCloseModal} onSubmit={handleSubmitLoan} />
      </ModalWrapper>

      {/* Payment Modal */}
      <Modal
        title="Make Loan Payment"
        open={isPaymentModalOpen}
        onCancel={handleClosePaymentModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePaymentSubmit}
          initialValues={{
            date: dayjs(),
            amount: 0
          }}
        >
          <Form.Item
            label="Payment Date"
            name="date"
            rules={[{ required: true, message: 'Please select payment date!' }]}
          >
            <DatePicker 
              className="w-full" 
              format="YYYY-MM-DD"
            />
          </Form.Item>
          
          <Form.Item
            label="Payment Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter payment amount!' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than 0!' }
            ]}
          >
            <InputNumber 
              className="w-full" 
              placeholder="Enter amount"
              min={0.01}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={handleClosePaymentModal}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={paymentLoading}
              className="bg-primary text-white"
            >
              Process Payment
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default RequestLoan;
